import React, { useState, useEffect, useRef } from "react";
import Error from "../../components/Error";
import mut from "../../hooks/mut";
import useDBO from "../../hooks/useDBO";
import useCTX from "../../hooks/useCTX";
import Search from "../../components/Search";
import Pagination from "../../components/Pagination";
import DateChoice from "../../components/DateChoice";
import VND from "../../components/VND";
import Lang from "../../components/Lang";
import NewStaffDialog from "../staff/components/NewStaffDialog.tsx";
import StopUserButton from "../../components/StopUserButton";
import {
  Users,
  Plus,
  ChevronDown,
  X,
  Shield,
  Loader2,
  CalendarDays,
  DollarSign,
  UserCog,
} from "lucide-react";

/**
 * Humanresources — Human resources management page at /manage/humanResources.
 * Displays all staff as card rows with inline role editing, salary,
 * manager assignment, and review dates. No MUI dependencies.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 3.0
 * @returns {React.ReactElement}
 */
export default function Humanresources() {
  const [status, setStatus] = useState(0);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [newStaffOpen, setNewStaffOpen] = useState(false);
  const data = useDBO(`/users-hr/${status}`);
  const { center, getStaffRole } = useCTX();

  if (data.error) return <Error />;
  if (data.loading) {
    return (
      <div className="flex items-center justify-center h-full max-w-[1600px] mx-auto bg-slate-50 text-slate-800">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-slate-300" />
          <p className="text-slate-400"><Lang w="loading" /></p>
        </div>
      </div>
    );
  }

  const staffList = data.get || [];
  const roles = center?.roles || [];

  const filtered = staffList
    .filter((s) => (filter === "" ? true : s.roles && s.roles.includes(filter)))
    .filter((s) => (search === "" ? true : s.name.toLowerCase().includes(search.toLowerCase())));

  const paged = filtered.slice(page * 10, page * 10 + 10);

  /**
   * Remove a role from a staff member and persist to server.
   * @param {Object} s - Staff member
   * @param {string} role - Role to remove
   */
  const removeRole = (s, role) => {
    const roles = s.roles.filter((r) => r !== role);
    mut("post", `/user/${s._id}`, { roles });
    data.update({ ...s, roles });
  };

  /**
   * Add a role to a staff member and persist to server.
   * @param {Object} s - Staff member
   * @param {string} role - Role to add
   */
  const addRole = (s, role) => {
    const roles = [...(s.roles || []), role];
    mut("post", `/user/${s._id}`, { roles });
    data.update({ ...s, roles });
  };

  const avatarColor = (str) => {
    const colors = [
      "bg-sky-100 text-sky-600",
      "bg-emerald-100 text-emerald-600",
      "bg-violet-100 text-violet-600",
      "bg-amber-100 text-amber-600",
      "bg-rose-100 text-rose-600",
      "bg-indigo-100 text-indigo-600",
      "bg-teal-100 text-teal-600",
      "bg-orange-100 text-orange-600",
    ];
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB");
  };

  return (
    <div className="flex flex-col h-full max-w-[1600px] mx-auto bg-slate-50 text-slate-800">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 pt-6 pb-4">
        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
          <Users size={20} className="text-teal-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">
            <Lang w="humanResources" />
          </h1>
          <p className="text-sm text-slate-400">
            {staffList.length} staff member{staffList.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mx-6 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status Tabs */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => { setStatus(0); setPage(0); }}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all duration-150 cursor-pointer ${
                status === 0
                  ? "bg-white text-teal-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Lang w="current" /> ({status === 0 ? staffList.length : ""})
            </button>
            <button
              onClick={() => { setStatus(1); setPage(0); }}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all duration-150 cursor-pointer ${
                status === 1
                  ? "bg-white text-teal-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Lang w="stopped" />
            </button>
          </div>

          {/* Search */}
          <div className="w-56">
            <Search search={search} setSearch={(v) => { setSearch(v); setPage(0); }} />
          </div>

          {/* Role Filter */}
          <RoleFilterSelect value={filter} setValue={(v) => { setFilter(v); setPage(0); }} roles={roles} />

          <div className="flex-1" />

          {/* Add Staff */}
          <button
            onClick={() => setNewStaffOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 transition-all duration-150 cursor-pointer"
          >
            <Plus size={16} />
            <Lang w="add" />
          </button>

          {/* Pagination */}
          <Pagination page={page} setPage={setPage} end={filtered.length} />
        </div>
      </div>

      {/* Staff List */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="flex flex-col gap-2">
          {paged.map((s) => (
            <StaffRow
              key={s._id}
              s={s}
              status={status}
              roles={roles}
              managers={getStaffRole("Manager")}
              data={data}
              onRemoveRole={removeRole}
              onAddRole={addRole}
              avatarColor={avatarColor}
              getInitials={getInitials}
              formatDate={formatDate}
            />
          ))}

          {paged.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <Users size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-400"><Lang w="noStaffFound" /></p>
            </div>
          )}
        </div>
      </div>

      {/* New Staff Dialog - reuse from staff page */}
      <NewStaffDialog open={newStaffOpen} setOpen={setNewStaffOpen} myStaff={data} />
    </div>
  );
}

/**
 * StaffRow — Single staff member displayed as a horizontal card row.
 * Shows avatar, name, start date, editable roles, manager, salary,
 * next review date, and stop action.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 1.0
 * @param {Object} props
 * @returns {React.ReactElement}
 */
function StaffRow({ s, status, roles, managers = [], data, onRemoveRole, onAddRole, avatarColor, getInitials, formatDate }) {
  const assignedRoles = s.roles || [];
  const availableRoles = roles.filter((r) => !assignedRoles.includes(r));
  const managerOptions = [{ _id: "", name: "None" }, ...managers.map((m) => ({ _id: m._id, name: m.name }))];

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 hover:border-slate-300 transition-all duration-150">
      <div className="flex items-start gap-4">
        {/* Avatar + Name */}
        <div className="flex items-center gap-3 min-w-[200px]">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(s.name)}`}
          >
            {getInitials(s.name)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-800 truncate">{s.name}</div>
            <div className="text-xs text-slate-400">{formatDate(s.createdAt)}</div>
          </div>
        </div>

        {/* Roles */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <Shield size={12} className="text-slate-400" />
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Roles</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {assignedRoles.map((role) => (
              <button
                key={role}
                onClick={() => onRemoveRole(s, role)}
                className="flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-600 border border-teal-200 rounded-md text-xs font-semibold hover:bg-teal-100 transition-all duration-150 cursor-pointer"
                title={`Remove ${role}`}
              >
                {role}
                <X size={10} />
              </button>
            ))}
            {availableRoles.length > 0 && (
              <RoleDropdown
                availableRoles={availableRoles}
                onAdd={(role) => onAddRole(s, role)}
              />
            )}
          </div>
        </div>

        {/* Manager */}
        <div className="w-[160px] flex-shrink-0">
          <div className="flex items-center gap-1 mb-1">
            <UserCog size={12} className="text-slate-400" />
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Manager</span>
          </div>
          <InlineManagerSelect
            value={s.reportsTo || ""}
            onChange={(reportsTo) => {
              mut("post", `/user/${s._id}`, { reportsTo });
              data.update({ ...s, reportsTo });
            }}
            options={managerOptions}
          />
        </div>

        {/* Salary */}
        <div className="w-[140px] flex-shrink-0">
          <div className="flex items-center gap-1 mb-1">
            <DollarSign size={12} className="text-slate-400" />
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Salary</span>
          </div>
          <VND
            value={s.salary ? s.salary : ""}
            setValue={(value) => data.update({ ...s, salary: value })}
            url={`/users-hr/${s._id}`}
            field="salary"
            size="small"
            label=""
          />
        </div>

        {/* Next Review */}
        <div className="w-[160px] flex-shrink-0">
          <div className="flex items-center gap-1 mb-1">
            <CalendarDays size={12} className="text-slate-400" />
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Review</span>
          </div>
          <DateChoice
            value={s.nextReview}
            setValue={(nextReview) => data.update({ ...s, nextReview })}
            url={`/users-hr/${s._id}`}
            field="nextReview"
            label=""
            fullWidth={false}
          />
        </div>

        {/* Status / Stop */}
        <div className="w-[80px] flex-shrink-0 flex items-center justify-center">
          {status === 1 ? (
            <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-1 rounded-md border border-red-200">
              <Lang w="stopped" />
            </span>
          ) : (
            <StopUserButton
              id={s._id}
              result={() => data.refresh()}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * RoleDropdown — Dropdown to add a role from the list of unassigned roles.
 * Uses click-outside detection instead of MUI Autocomplete.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 1.0
 * @param {Object} props
 * @param {string[]} props.availableRoles - Roles not yet assigned
 * @param {Function} props.onAdd - Callback when a role is selected
 * @returns {React.ReactElement}
 */
function RoleDropdown({ availableRoles, onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-0.5 bg-slate-50 text-slate-500 border border-slate-200 rounded-md text-xs font-semibold hover:bg-slate-100 hover:border-slate-300 transition-all duration-150 cursor-pointer"
      >
        <Plus size={10} />
        Add
        <ChevronDown size={10} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[140px]">
          {availableRoles.map((role) => (
            <button
              key={role}
              onClick={() => {
                onAdd(role);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-teal-600 transition-colors cursor-pointer"
            >
              {role}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * RoleFilterSelect — Compact dropdown for filtering by role in the toolbar.
 * Reads available roles from center context.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 1.0
 * @param {Object} props
 * @param {string} props.value - Currently selected role filter
 * @param {Function} props.setValue - Setter for role filter
 * @param {string[]} props.roles - Available roles from center context
 * @returns {React.ReactElement}
 */
function RoleFilterSelect({ value, setValue, roles }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-[10px] text-sm text-slate-600 hover:bg-white hover:border-slate-300 transition-all duration-150 cursor-pointer min-w-[140px]"
      >
        <Shield size={14} className="text-slate-400" />
        <span className="flex-1 text-left truncate">
          {value || "All roles"}
        </span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px]">
          <button
            onClick={() => { setValue(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors cursor-pointer ${
                value === "" ? "text-teal-600 font-semibold bg-slate-50" : "text-slate-600 hover:bg-slate-50"
              }`}
          >
            All roles
          </button>
          {roles.map((role) => (
            <button
              key={role}
              onClick={() => { setValue(role); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors cursor-pointer ${
                value === role ? "text-teal-600 font-semibold bg-slate-50" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {role}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * InlineManagerSelect — Compact dropdown for selecting a staff member's manager.
 * Uses click-outside detection, no external dependencies.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 1.0
 * @param {Object} props
 * @param {string} props.value - Current manager _id
 * @param {Function} props.onChange - Callback with new manager _id
 * @param {Array<{_id: string, name: string}>} props.options - Manager options
 * @returns {React.ReactElement}
 */
function InlineManagerSelect({ value, onChange, options = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selected = options.find((o) => o._id === value);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 hover:bg-white hover:border-slate-300 transition-all duration-150 cursor-pointer"
      >
        <span className="flex-1 text-left truncate">
          {selected && selected._id ? selected.name : "None"}
        </span>
        <ChevronDown size={12} className="text-slate-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1 w-full min-w-[150px]">
          {options.map((opt) => (
            <button
              key={opt._id || "none"}
              onClick={() => {
                onChange(opt._id);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                value === opt._id
                  ? "text-teal-600 font-semibold bg-slate-50"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {opt.name || "None"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
